import { useEffect, useState } from "react";

import { Loader2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  ARTIST_AUDIT_KIND_HINT,
  artistAuditKindLabel,
  summariseArtistFinding,
} from "@/lib/tina/artist-audit";
import type { ArtistAuditFindingRow, ArtistAuditKind } from "@/types/tina";

interface DismissFindingDialogProps {
  /** The finding to dismiss; `null` closes the dialog. */
  finding: ArtistAuditFindingRow | null;
  pending: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (note: string) => void;
}

/**
 * The one confirmation on this page, because a dismissal is permanent.
 *
 * It re-states the finding rather than asking "are you sure?" about an id, and it says out
 * loud what dismissal means: the row is gone until the audit sees a materially different
 * version of it — an artist joining or leaving the pair, or the severity rising. A changed
 * follower count, a new booking on the same artist or a reworded name will NOT bring it
 * back, by design, or the 1,239 name pairs would return every morning.
 *
 * A dismissal here does NOT resolve a duplicate — only a merge on the artist page does. A
 * dismissed true duplicate stays in the corpus. Spec section 9.3.
 *
 * The note is optional and free text. It is the only record of why a curator decided a
 * finding was acceptable, and the next person to look at these artists reads it.
 */
export function DismissFindingDialog({
  finding,
  pending,
  onOpenChange,
  onConfirm,
}: DismissFindingDialogProps) {
  const [note, setNote] = useState("");

  // A fresh note per finding: carrying the previous one over would attribute one
  // curator's reasoning to a different artist.
  useEffect(() => {
    setNote("");
  }, [finding?.id]);

  const hint = finding
    ? ARTIST_AUDIT_KIND_HINT[finding.kind as ArtistAuditKind]
    : undefined;

  return (
    <Dialog open={finding !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Dismiss this finding?</DialogTitle>
          <DialogDescription>
            It will not appear again unless the artists involved change, or the
            finding gets worse. There is no undo on this page.
          </DialogDescription>
        </DialogHeader>

        {finding ? (
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                variant={
                  finding.severity === "high" ? "destructive" : "secondary"
                }
                className="text-[10px] uppercase"
              >
                {finding.severity}
              </Badge>
              <Badge variant="outline" className="text-[10px] font-normal">
                {artistAuditKindLabel(finding.kind)}
              </Badge>
              <span className="font-mono text-[10px] text-muted-foreground">
                {finding.fingerprint}
              </span>
            </div>
            <p className="text-sm">{summariseArtistFinding(finding)}</p>
            {hint ? (
              <p className="text-xs text-muted-foreground">{hint}</p>
            ) : null}
            <Textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Why is this acceptable? Optional, but the next curator reads it."
              className="min-h-[80px] text-sm"
            />
          </div>
        ) : null}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={pending}
          >
            Keep it
          </Button>
          <Button onClick={() => onConfirm(note)} disabled={pending}>
            {pending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Dismiss for good
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
